import { useCallback, useMemo } from 'react';
import { format, subDays } from 'date-fns';

import { useListFilters } from '@/client/hooks/useListFilters';
import type {
  AdminUserListFilters,
  AdminUserSortKey,
  AdminUserStatus,
  AdminUsersListParams,
} from '@/client/types/admin/users-orgs';

export const USER_STATUSES: { key: AdminUserStatus; label: string }[] = [
  { key: 'active', label: 'Active' },
  { key: 'inactive', label: 'Inactive' },
  { key: 'suspended', label: 'Suspended' },
  { key: 'deleted', label: 'Deleted' },
];

export const CREATED_PRESETS = [
  { key: '7d', label: 'Last 7 days', days: 7 },
  { key: '30d', label: 'Last 30 days', days: 30 },
  { key: '90d', label: 'Last 90 days', days: 90 },
  { key: '365d', label: 'Last 12 months', days: 365 },
] as const;

const SORT_KEYS: AdminUserSortKey[] = ['name', 'email', 'created', 'role', 'status', 'uid'];

// Every key lives in the URL so a view survives refresh and can be shared.
// `created` holds a preset key (kept relative on reload) or 'custom' + from/to.
const DEFAULTS = {
  roles: '',
  status: '',
  email: 'any',
  mfa: 'any',
  created: '',
  from: '',
  to: '',
  sort: 'created',
  order: 'desc',
};

const csvNumbers = (raw: string) =>
  raw.split(',').map(Number).filter((n) => Number.isInteger(n) && n > 0);

const day = (d: Date) => format(d, 'yyyy-MM-dd');

export function createdRange(created: string, from: string, to: string) {
  if (created === 'custom') return { start: from || undefined, end: to || undefined };
  const preset = CREATED_PRESETS.find((p) => p.key === created);
  if (!preset) return { start: undefined, end: undefined };
  const today = new Date();
  return { start: day(subDays(today, preset.days - 1)), end: day(today) };
}

export function useUserListState() {
  const list = useListFilters({ initialFilters: DEFAULTS, initialLimit: 20, debounceMs: 400 });
  const { filters: f } = list;

  const roleIds = useMemo(() => csvNumbers(f.roles), [f.roles]);
  const statuses = useMemo(
    () => f.status.split(',').filter((s): s is AdminUserStatus => USER_STATUSES.some((u) => u.key === s)),
    [f.status],
  );
  const sortBy: AdminUserSortKey = SORT_KEYS.includes(f.sort as AdminUserSortKey)
    ? (f.sort as AdminUserSortKey)
    : 'created';
  const sortOrder: 'asc' | 'desc' = f.order === 'asc' ? 'asc' : 'desc';

  /** Exactly what "these filters" means to the list, bulk and export. */
  const listFilters = useMemo<AdminUserListFilters>(() => {
    const { start, end } = createdRange(f.created, f.from, f.to);
    return {
      status: statuses.join(',') || 'any',
      uartids: roleIds.length ? roleIds.join(',') : undefined,
      email_verified: f.email === 'verified' ? 1 : f.email === 'unverified' ? 0 : undefined,
      mfa: f.mfa === 'on' || f.mfa === 'off' ? f.mfa : undefined,
      search: list.debouncedSearchTerm || undefined,
      start_date: start,
      end_date: end,
    };
  }, [statuses, roleIds, f.email, f.mfa, f.created, f.from, f.to, list.debouncedSearchTerm]);

  const params = useMemo<AdminUsersListParams>(
    () => ({ ...listFilters, sort_by: sortBy, sort_order: sortOrder, page: list.page, page_size: list.limit }),
    [listFilters, sortBy, sortOrder, list.page, list.limit],
  );

  const { setFilters, handleFilterChange } = list;

  const toggleSort = useCallback(
    (field: string) => {
      const next = field as AdminUserSortKey;
      // Dates read newest-first by default; text columns A→Z.
      const order = sortBy === next ? (sortOrder === 'asc' ? 'desc' : 'asc') : next === 'created' ? 'desc' : 'asc';
      setFilters({ sort: next, order }, { resetPage: true });
    },
    [sortBy, sortOrder, setFilters],
  );

  const setRoles = useCallback((ids: number[]) => handleFilterChange('roles', ids.join(',')), [handleFilterChange]);
  const setStatuses = useCallback(
    (keys: AdminUserStatus[]) => handleFilterChange('status', keys.join(',')),
    [handleFilterChange],
  );
  const setCreated = useCallback(
    (created: string, from = '', to = '') => {
      setFilters(
        { created, from: created === 'custom' ? from : '', to: created === 'custom' ? to : '' },
        { resetPage: true },
      );
    },
    [setFilters],
  );

  const hasFilters =
    roleIds.length > 0 || statuses.length > 0 || f.email !== 'any' || f.mfa !== 'any' || Boolean(f.created) || Boolean(list.debouncedSearchTerm);

  return {
    ...list,
    roleIds,
    statuses,
    email: f.email as 'any' | 'verified' | 'unverified',
    mfa: f.mfa as 'any' | 'on' | 'off',
    created: f.created,
    createdFrom: f.from,
    createdTo: f.to,
    sortBy,
    sortOrder,
    listFilters,
    params,
    hasFilters,
    toggleSort,
    setRoles,
    setStatuses,
    setEmail: (v: string) => handleFilterChange('email', v),
    setMfa: (v: string) => handleFilterChange('mfa', v),
    setCreated,
  };
}
